/**
 * Public live-status page (Roadmap G).
 * Routed at /status — anonymous, polls /api/public/status every 60s.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, AlertOctagon, Activity, RefreshCcw } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  operational: { color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", Icon: CheckCircle2, label: "Operational" },
  monitoring: { color: "text-sky-700", bg: "bg-sky-50", border: "border-sky-200", Icon: Activity, label: "Monitoring" },
  degraded: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", Icon: AlertTriangle, label: "Degraded" },
  outage: { color: "text-rose-700", bg: "bg-rose-50", border: "border-rose-200", Icon: AlertOctagon, label: "Outage" },
};

function Pill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.monitoring;
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.color} ${s.bg} ${s.border}`} data-testid={`status-pill-${status}`}>
      <Icon size={12} /> {s.label}
    </span>
  );
}

export default function Status() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/public/status`);
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e.message || "Status check failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  const overall = data?.overall || "monitoring";
  const overallStyle = STATUS_STYLES[overall] || STATUS_STYLES.monitoring;
  const OverallIcon = overallStyle.Icon;

  return (
    <main className="min-h-screen bg-[#f7f5fb] px-4 py-12" data-testid="status-page">
      <div className="mx-auto max-w-3xl">
        <header className="mb-8">
          <Link to="/" className="text-xs font-semibold text-ek-ink/55 hover:text-ek-purple">← ERIO</Link>
          <h1 className="mt-3 font-display text-4xl font-semibold text-ek-ink">ERIO live status</h1>
          <p className="mt-2 text-sm text-ek-ink/65">
            Real-time service health and uptime for <span className="font-mono">erio.ekselens.com</span>.
            Updated every 60 seconds.
          </p>
        </header>

        {loading && !data && (
          <p className="text-sm text-ek-ink/55" data-testid="status-loading">Checking…</p>
        )}
        {err && !data && (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="status-error">
            Live status couldn&apos;t be reached. Please check back in a moment.
          </p>
        )}

        {data && (
          <>
            <section className={`rounded-2xl border ${overallStyle.border} ${overallStyle.bg} p-6`}>
              <div className="flex items-center gap-3">
                <div className={`rounded-full p-2 ${overallStyle.color} bg-white`}>
                  <OverallIcon size={22} />
                </div>
                <div className="flex-1">
                  <p className={`font-display text-2xl font-semibold ${overallStyle.color}`} data-testid="status-overall">
                    {overallStyle.label}
                  </p>
                  <p className="mt-0.5 text-xs text-ek-ink/55">
                    Checked at {new Date(data.checked_at).toLocaleTimeString()} · {data.window_days}-day uptime <span className="font-semibold" data-testid="status-uptime">{data.uptime_pct.toFixed(3)}%</span>
                  </p>
                </div>
                <button onClick={load} className="rounded-md border border-black/10 bg-white px-2.5 py-1 text-xs font-semibold text-ek-ink hover:border-ek-purple/40" data-testid="status-refresh">
                  <RefreshCcw size={12} className="inline" /> Refresh
                </button>
              </div>
            </section>

            <section className="mt-6 rounded-2xl border border-black/5 bg-white p-5">
              <h2 className="text-sm font-semibold text-ek-ink">Components</h2>
              <ul className="mt-3 divide-y divide-black/5">
                {data.components.map((c) => (
                  <li key={c.id} className="flex items-center justify-between py-2 text-sm" data-testid={`status-component-${c.id}`}>
                    <span className="text-ek-ink/80">{c.label}</span>
                    <Pill status={c.status} />
                  </li>
                ))}
              </ul>
            </section>

            {data.scheduler && data.scheduler.length > 0 && (
              <section className="mt-6 rounded-2xl border border-black/5 bg-white p-5">
                <h2 className="text-sm font-semibold text-ek-ink">Scheduled jobs</h2>
                <ul className="mt-3 divide-y divide-black/5">
                  {data.scheduler.map((s) => (
                    <li key={s.job_id} className="flex items-center justify-between gap-2 py-2 text-xs" data-testid={`status-job-${s.job_id}`}>
                      <span className="text-ek-ink/80">{s.label}</span>
                      <span className="text-[10px] text-ek-ink/55">{s.cadence} · last run {s.age} ago</span>
                      <Pill status={s.status === "ok" ? "operational" : "degraded"} />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {data.last_incident && (
              <section className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/40 p-5">
                <h2 className="text-sm font-semibold text-amber-900">Most recent incident</h2>
                <p className="mt-1 text-sm font-semibold text-ek-ink" data-testid="status-last-incident-title">{data.last_incident.title}</p>
                <p className="mt-0.5 text-xs text-ek-ink/70">{data.last_incident.summary}</p>
                <p className="mt-2 text-[11px] text-ek-ink/55">
                  Severity: <span className="font-mono">{data.last_incident.severity}</span> · Status: <span className="font-mono">{data.last_incident.status}</span>
                  {data.last_incident.started_at && <> · Started {new Date(data.last_incident.started_at).toLocaleString()}</>}
                  {data.last_incident.resolved_at && <> · Resolved {new Date(data.last_incident.resolved_at).toLocaleString()}</>}
                </p>
              </section>
            )}

            <p className="mt-8 text-center text-xs text-ek-ink/40">
              Powered by ERIO · <Link to="/trust" className="hover:underline">Trust Centre →</Link>
            </p>
          </>
        )}
      </div>
    </main>
  );
}
